import {
  ArrowLeft,
  ShieldCheck,
  Scale,
  Cookie,
  RefreshCcw,
  Printer,
  ListOrdered,
  FileWarning,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { IS_LEGAL_DRAFT, LEGAL_COMPANY_INFO } from "./legalConfig";
import { LegalFooter } from "./LegalFooter";
import {
  getCookieConsent,
  openCookiePreferences,
} from "./CookieConsentBanner";

export interface TocItem {
  id: string;
  label: string;
}

/**
 * Selo / nota discreta no fim da seção para itens que exigem validação jurídica.
 * Só é renderizado quando `VITE_LEGAL_DRAFT=true` (IS_LEGAL_DRAFT), permitindo
 * ocultar avisos de desenvolvimento em produção sem editar o texto.
 */
const LegalReviewNote = ({ children }: { children: React.ReactNode }) => {
  if (!IS_LEGAL_DRAFT) return null;

  return (
    <aside
      aria-label="Nota de revisão jurídica"
      className="no-print mt-4 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50/90 px-3.5 py-2.5 text-xs leading-normal text-amber-950 dark:border-amber-800/80 dark:bg-amber-950/50 dark:text-amber-100"
    >
      <span className="inline-flex shrink-0 items-center rounded border border-amber-400 bg-amber-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-amber-950 dark:border-amber-700 dark:bg-amber-900 dark:text-amber-100">
        REVISAR COM ADVOGADO
      </span>
      <span className="flex-1">{children}</span>
    </aside>
  );
};

const LegalPageShell = ({
  title,
  subtitle,
  icon,
  toc,
  children,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  toc?: TocItem[];
  children: React.ReactNode;
}) => {
  const scrollToSection = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string,
  ) => {
    e.preventDefault();
    const target = document.getElementById(sectionId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      target.focus({ preventScroll: true });
    }
  };

  return (
    <div className="legal-page-shell h-screen overflow-y-auto flex flex-col bg-background text-foreground">
      <header className="legal-page-header border-b border-border bg-card/95 backdrop-blur-sm sticky top-0 z-30 px-4 sm:px-8 py-4">
        <div className="max-w-[72ch] mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
              {icon}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold tracking-tight text-foreground truncate">
                {title}
              </h1>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
          </div>

          <div className="no-print flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => window.print()}
              className="text-xs hidden sm:inline-flex"
              aria-label="Imprimir documento"
            >
              <Printer className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
              Imprimir
            </Button>
            <Button asChild variant="outline" size="sm" className="text-xs">
              <a href="#/">
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                Voltar ao CRM
              </a>
            </Button>
          </div>
        </div>
      </header>

      <main
        id="main-content"
        className="legal-page-content flex-1 w-full max-w-[72ch] mx-auto px-4 sm:px-6 py-10 space-y-10 text-[16px] leading-[1.75] text-foreground"
      >
        {IS_LEGAL_DRAFT && (
          <div
            role="note"
            aria-label="Aviso de modo de rascunho jurídico"
            className="no-print p-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-100 text-xs leading-relaxed space-y-1.5"
          >
            <p className="font-semibold flex items-center gap-1.5 text-sm">
              <FileWarning className="w-4 h-4 shrink-0" aria-hidden="true" />
              Modo de Revisão Jurídica Ativo (VITE_LEGAL_DRAFT=true)
            </p>
            <p>
              Este documento contém marcadores{" "}
              <code className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900 font-mono text-amber-950 dark:text-amber-100">
                {"{{PLACEHOLDER}}"}
              </code>{" "}
              e notas de rodapé de seção sinalizadas com{" "}
              <strong>REVISAR COM ADVOGADO</strong>. Para ocultar estes avisos em
              produção sem editar o texto, defina{" "}
              <code className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900 font-mono">
                VITE_LEGAL_DRAFT=false
              </code>
              .
            </p>
          </div>
        )}

        {toc && toc.length > 0 && (
          <nav
            aria-label="Índice do documento"
            className="no-print rounded-xl border border-border bg-card p-5 space-y-3 shadow-2xs"
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <ListOrdered className="w-4 h-4 text-primary" aria-hidden="true" />
              <span>Índice de Seções</span>
            </div>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {toc.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => scrollToSection(e, item.id)}
                    className="text-foreground hover:text-primary underline-offset-4 hover:underline transition-colors block py-0.5"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {children}
      </main>

      <LegalFooter />
    </div>
  );
};

// ─── 1. POLÍTICA DE PRIVACIDADE (/privacidade) ───────────────────────────────
const PRIVACY_TOC: TocItem[] = [
  { id: "priv-identificacao", label: "1. Identificação e Encarregado (DPO)" },
  { id: "priv-papeis", label: "2. Papéis na LGPD: Controlador vs. Operador" },
  { id: "priv-dados-bases", label: "3. Dados Coletados, Finalidades e Bases Legais" },
  { id: "priv-suboperadores", label: "4. Suboperadores e Transferência Internacional" },
  { id: "priv-retencao", label: "5. Retenção e Descarte de Dados" },
  { id: "priv-direitos", label: "6. Direitos do Titular (Art. 18 da LGPD)" },
];

export const PrivacyPolicyPage = () => {
  return (
    <LegalPageShell
      title="Política de Privacidade e Proteção de Dados"
      subtitle={`Versão ${LEGAL_COMPANY_INFO.versaoPoliticaPrivacidade} • Última atualização: ${LEGAL_COMPANY_INFO.dataUltimaAtualizacao}`}
      icon={<ShieldCheck className="w-5 h-5" aria-hidden="true" />}
      toc={PRIVACY_TOC}
    >
      <section
        id="priv-identificacao"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          1. Identificação da Empresa e do Encarregado (DPO)
        </h2>
        <p>
          Esta Política de Privacidade descreve como{" "}
          <strong>{LEGAL_COMPANY_INFO.razaoSocial}</strong> (nome fantasia{" "}
          <strong>{LEGAL_COMPANY_INFO.nomeFantasia}</strong>), inscrita no CNPJ
          sob o nº <strong>{LEGAL_COMPANY_INFO.cnpj}</strong>, com sede em{" "}
          <strong>{LEGAL_COMPANY_INFO.enderecoCompleto}</strong>, realiza o
          tratamento de dados pessoais no âmbito da plataforma Dotus CRM, em
          conformidade com a Lei Geral de Proteção de Dados Pessoais — LGPD (Lei
          nº 13.709/2018) e o Marco Civil da Internet (Lei nº 12.965/2014).
        </p>
        <p>
          Em cumprimento ao <strong>Art. 41, § 1º da Lei nº 13.709/2018</strong>
          , o Encarregado pelo Tratamento de Dados Pessoais (DPO) é{" "}
          <strong>{LEGAL_COMPANY_INFO.nomeDpo}</strong>, disponível pelo canal
          oficial: <strong>{LEGAL_COMPANY_INFO.emailDpo}</strong>.
        </p>
      </section>

      <section
        id="priv-papeis"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          2. Papéis na LGPD: Controlador vs. Operador (Art. 5º, VI e VII da LGPD)
        </h2>
        <p>
          A plataforma Dotus CRM atua em dois fluxos distintos de tratamento de
          dados pessoais, conforme definido no Art. 5º, incisos VI e VII da Lei
          nº 13.709/2018:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-border bg-card space-y-2">
            <h3 className="font-semibold text-foreground text-base">
              Fluxo A — Dotus como CONTROLADORA (Art. 5º, VI da LGPD)
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Quando tratamos os dados cadastrais dos nossos próprios clientes
              contratantes, representantes comerciais e usuários operadores do
              CRM (nome, e-mail de login, credenciais de acesso, registros de
              faturamento e registros de acesso à aplicação). Neste fluxo,
              compete à Dotus a decisão sobre o tratamento.
            </p>
          </div>
          <div className="p-4 rounded-xl border border-border bg-card space-y-2">
            <h3 className="font-semibold text-foreground text-base">
              Fluxo B — Dotus como OPERADORA (Art. 5º, VII da LGPD)
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Quando a empresa cliente utiliza o CRM para armazenar e gerenciar
              seus próprios leads, contatos, empresas, notas, histórico de
              negócios e chamadas. Neste fluxo, o{" "}
              <strong className="text-foreground">
                Cliente Contratante é o Controlador
              </strong>{" "}
              e a{" "}
              <strong className="text-foreground">Dotus atua como Operadora</strong>
              , tratando os dados estritamente segundo as instruções do
              Controlador (Art. 39 da LGPD) e o Acordo de Processamento de Dados
              (DPA).
            </p>
          </div>
        </div>
        <LegalReviewNote>
          Validar a delimitação contratual entre os fluxos de Controladora e
          Operadora conforme o modelo comercial definitivo (SaaS multi-tenant vs.
          uso interno do grupo).
        </LegalReviewNote>
      </section>

      <section
        id="priv-dados-bases"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          3. Dados Coletados, Finalidades e Bases Legais (Arts. 7º e 9º da LGPD)
        </h2>
        <p>
          Abaixo estão as categorias de dados tratados na plataforma e suas
          respectivas hipóteses legais:
        </p>
        <ul className="list-disc pl-6 space-y-3">
          <li>
            <strong>Dados de Conta e Autenticação de Usuários:</strong> Nome,
            sobrenome, endereço de e-mail corporativo, hash de senha e papel de
            acesso. <em>Finalidade:</em> prover acesso autenticado e controle de
            permissões por tenant. <em>Base legal:</em> Execução de contrato
            (Art. 7º, V da LGPD).
          </li>
          <li>
            <strong>Registros de Acesso a Aplicações e Segurança:</strong>{" "}
            Endereço IP, data e hora de acesso e identificadores de sessão.{" "}
            <em>Finalidade:</em> cumprimento de dever legal de guarda de logs de
            aplicação e prevenção a fraudes. <em>Base legal:</em> Cumprimento de
            obrigação legal ou regulatória (Art. 7º, II da LGPD c/c{" "}
            <strong>
              Art. 15 da Lei nº 12.965/2014 — Marco Civil da Internet
            </strong>
            ).
          </li>
          <li>
            <strong>
              Dados de Leads e Contatos Cadastrados pelos Clientes (Fluxo
              Operador):
            </strong>{" "}
            Nome, telefone, e-mail, cargo, empresa, notas comerciais e origem do
            lead (ex.: formulários, API, Meta Lead Ads).{" "}
            <em>Finalidade:</em> gestão do relacionamento comercial pelo
            Controlador. <em>Base legal:</em> definida pelo Controlador (Art.
            7º, I — Consentimento, Art. 7º, V — Procedimentos preliminares a
            contrato, ou Art. 7º, IX — Legítimo Interesse).
          </li>
          <li>
            <strong>
              Gravações de Chamadas Telefônicas / WhatsApp e Transcrições:
            </strong>{" "}
            Áudio de chamadas e resumos/transcrições automatizados.{" "}
            <em>Regra técnica de proteção:</em> O armazenamento de áudio e a
            transcrição ficam <strong>desligados por padrão</strong> no sistema,
            só sendo ativados caso o Controlador habilite expressamente a
            funcionalidade e configure o aviso prévio de gravação ao titular.
          </li>
        </ul>
        <LegalReviewNote>
          Definir com assessoria jurídica a base legal aplicável para gravação e
          transcrição de chamadas em cada operação (Art. 7º, I — Consentimento;
          Art. 7º, V — Execução de contrato; ou Art. 7º, IX — Legítimo Interesse
          com LIA).
        </LegalReviewNote>
      </section>

      <section
        id="priv-suboperadores"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          4. Compartilhamento com Suboperadores e Transferência Internacional
          (Art. 33 da LGPD)
        </h2>
        <p>
          Para a execução técnica dos serviços, utilizamos suboperadores de
          infraestrutura e processamento, que podem armazenar ou processar dados
          em servidores localizados no exterior (como nos Estados Unidos),
          observando o <strong>Art. 33 da Lei nº 13.709/2018</strong> e a
          Resolução CD/ANPD nº 19/2024:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Supabase Inc. (AWS):</strong> Banco de dados PostgreSQL,
            autenticação e armazenamento de arquivos.
          </li>
          <li>
            <strong>OpenAI, L.L.C. / Google LLC (Gemini):</strong> Processamento
            de transcrição de áudio e resumo de chamadas (exclusivamente quando
            a flag de gravação e transcrição estiver habilitada pelo tenant).
          </li>
          <li>
            <strong>Postmark (ActiveCampaign, LLC):</strong> Processamento de
            webhooks de e-mails recebidos (quando configurado).
          </li>
          <li>
            <strong>API4COM / Gateway VoIP e WhatsApp:</strong> Intermediação de
            chamadas quando acionadas pelo operador.
          </li>
        </ul>
        <LegalReviewNote>
          Verificar os instrumentos de transferência internacional de dados
          (cláusulas-padrão contratuais da Resolução CD/ANPD nº 19/2024) junto
          aos suboperadores listados e confirmar a região exata do projeto
          Supabase.
        </LegalReviewNote>
      </section>

      <section
        id="priv-retencao"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          5. Política de Retenção e Descarte de Dados (Arts. 15 e 16 da LGPD e
          Art. 15 do Marco Civil)
        </h2>
        <p>
          O término do tratamento e a eliminação dos dados pessoais seguem os{" "}
          <strong>Arts. 15 e 16 da Lei nº 13.709/2018</strong>, separando-se os
          prazos de guarda conforme a natureza jurídica do registro:
        </p>
        <ul className="list-disc pl-6 space-y-3">
          <li>
            <strong>
              Registros de Acesso a Aplicações (Obrigação Legal):
            </strong>{" "}
            Mantidos sob sigilo, em ambiente controlado e de segurança, pelo
            prazo mínimo obrigatório de <strong>6 (seis) meses</strong>, nos
            termos do{" "}
            <strong>
              Art. 15 da Lei nº 12.965/2014 (Marco Civil da Internet)
            </strong>
            . Estes registros são conservados para cumprimento de obrigação
            legal (<strong>Art. 16, I da LGPD</strong>) e não são eliminados
            antes desse período mínimo, mesmo em caso de exclusão ou
            anonimização de conta.
          </li>
          <li>
            <strong>Gravações de Áudio e Transcrições de Chamadas:</strong>{" "}
            Quando habilitadas pelo tenant, ficam retidas pelo prazo
            configurável de{" "}
            <strong>{LEGAL_COMPANY_INFO.prazoRetencaoGravacoes}</strong>, sendo
            descartadas após o término desse período (
            <strong>Art. 15, I e II c/c Art. 16, caput da LGPD</strong>),
            ressalvada a conservação estritamente necessária para cumprimento de
            obrigação legal ou regulatória (<strong>Art. 16, I da LGPD</strong>)
            ou o tratamento destinado ao exercício regular de direitos em
            processo judicial, administrativo ou arbitral (
            <strong>Art. 7º, VI da LGPD</strong>).
          </li>
          <li>
            <strong>Leads Inativos, Notas e Dados de Negócio:</strong> Mantidos
            enquanto durar a relação contratual com o tenant ou pelo prazo
            configurado de{" "}
            <strong>{LEGAL_COMPANY_INFO.prazoRetencaoLeadsInativos}</strong>.
          </li>
        </ul>
        <LegalReviewNote>
          Revisar a fundamentação jurídica para eventual guarda de gravações após
          o prazo operacional: o Art. 16, I da LGPD autoriza conservação pós-término
          para obrigação legal ou regulatória, enquanto a retenção cautelar por
          prazos prescricionais apoia-se no Art. 7º, VI da LGPD (exercício
          regular de direitos). Definir também os prazos definitivos nos
          placeholders de retenção.
        </LegalReviewNote>
      </section>

      <section
        id="priv-direitos"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4"
      >
        <h2 className="text-lg font-bold text-foreground">
          6. Direitos do Titular de Dados Pessoais (Art. 18 da LGPD)
        </h2>
        <p>
          Nos termos do <strong>Art. 18 da Lei nº 13.709/2018</strong>, o
          titular dos dados pessoais tem direito a obter do Controlador, em
          relação aos dados do titular por ele tratados, a qualquer momento e
          mediante requisição:
        </p>
        <ol className="list-decimal pl-6 space-y-2">
          <li>Confirmação da existência de tratamento (Art. 18, I);</li>
          <li>Acesso aos dados (Art. 18, II);</li>
          <li>
            Correção de dados incompletos, inexatos ou desatualizados (Art. 18,
            III);
          </li>
          <li>
            Anonimização, bloqueio ou eliminação de dados desnecessários,
            excessivos ou tratados em desconformidade com o disposto na LGPD
            (Art. 18, IV);
          </li>
          <li>
            Portabilidade dos dados a outro fornecedor de serviço ou produto,
            mediante requisição expressa, de acordo com a regulamentação da
            autoridade nacional, observados os segredos comercial e industrial
            (Art. 18, V);
          </li>
          <li>
            Eliminação dos dados pessoais tratados com o consentimento do
            titular, exceto nas hipóteses previstas no Art. 16 da LGPD (Art. 18,
            VI);
          </li>
          <li>
            Informação das entidades públicas e privadas com as quais o
            controlador realizou uso compartilhado de dados (Art. 18, VII);
          </li>
          <li>
            Informação sobre a possibilidade de não fornecer consentimento e
            sobre as consequências da negativa (Art. 18, VIII);
          </li>
          <li>
            Revogação do consentimento, nos termos do § 5º do Art. 8º da LGPD
            (Art. 18, IX).
          </li>
        </ol>
        <p>
          Para exercer seus direitos perante a Dotus, envie solicitação para{" "}
          <strong>{LEGAL_COMPANY_INFO.emailDpo}</strong>. O prazo de atendimento
          às requisições é de{" "}
          <strong>{LEGAL_COMPANY_INFO.prazoRespostaTitular}</strong> (observado
          o Art. 19, II da LGPD). Quando a Dotus atuar exclusivamente como{" "}
          <strong>Operadora</strong> de dados de um cliente empresarial,
          encaminharemos a solicitação ao respectivo Controlador responsável.
        </p>
      </section>
    </LegalPageShell>
  );
};

// ─── 2. TERMOS DE SERVIÇO (/termos) ──────────────────────────────────────────
const TERMS_TOC: TocItem[] = [
  { id: "termos-partes", label: "1. Partes Contratantes e Objeto" },
  { id: "termos-natureza", label: "2. Natureza da Contratação (B2B vs. B2C)" },
  { id: "termos-obrigacoes", label: "3. Obrigações do Usuário e LGPD" },
  { id: "termos-responsabilidade", label: "4. Responsabilidade dos Agentes (Arts. 39 e 42)" },
  { id: "termos-isolamento", label: "5. Isolamento Multi-Tenant e Credenciais" },
  { id: "termos-foro", label: "6. Foro e Legislação Aplicável" },
];

export const TermsOfServicePage = () => {
  return (
    <LegalPageShell
      title="Termos e Condições de Uso do Dotus CRM"
      subtitle={`Versão ${LEGAL_COMPANY_INFO.versaoTermosUso} • Última atualização: ${LEGAL_COMPANY_INFO.dataUltimaAtualizacao}`}
      icon={<Scale className="w-5 h-5" aria-hidden="true" />}
      toc={TERMS_TOC}
    >
      <section
        id="termos-partes"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          1. Partes Contratantes e Objeto
        </h2>
        <p>
          Estes Termos de Uso regulam a utilização do software como serviço
          (SaaS) <strong>Dotus CRM</strong>, disponibilizado por{" "}
          <strong>{LEGAL_COMPANY_INFO.razaoSocial}</strong>, inscrita no CNPJ
          sob o nº <strong>{LEGAL_COMPANY_INFO.cnpj}</strong>, com endereço em{" "}
          <strong>{LEGAL_COMPANY_INFO.enderecoCompleto}</strong>.
        </p>
        <p>
          A relação contratual rege-se pelos princípios da liberdade contratual,
          força obrigatória e boa-fé objetiva previstos nos{" "}
          <strong>
            Arts. 421, 421-A e 422 da Lei nº 10.406/2002 (Código Civil)
          </strong>
          .
        </p>
      </section>

      <section
        id="termos-natureza"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          2. Natureza da Contratação (Variantes B2B e B2C)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-border bg-card space-y-2">
            <h3 className="font-semibold text-foreground text-base">
              Variante A — Contratação Exclusivamente Empresarial (B2B)
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              O Dotus CRM é uma ferramenta corporativa destinada exclusivamente
              a pessoas jurídicas e profissionais no exercício de sua atividade
              produtiva/comercial (insumo empresarial). Nessa modalidade, a
              relação é estritamente empresarial e regida pelo{" "}
              <strong className="text-foreground">
                Código Civil (Lei nº 10.406/2002, Art. 421-A)
              </strong>
              , não se aplicando o Código de Defesa do Consumidor (Lei nº
              8.078/1990), salvo hipótese excepcional de vulnerabilidade técnica
              ou econômica reconhecida.
            </p>
          </div>
          <div className="p-4 rounded-xl border border-border bg-card space-y-2">
            <h3 className="font-semibold text-foreground text-base">
              Variante B — Contratação por Consumidor Final / Equiparado (B2C)
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Caso a plataforma seja contratada por pessoa física ou
              microempreendedor na condição de destinatário final (Art. 2º da
              Lei nº 8.078/1990 — CDC), aplicam-se as garantias previstas no
              Código de Defesa do Consumidor e no Decreto nº 7.962/2013
              (comércio eletrônico).
            </p>
          </div>
        </div>
        <LegalReviewNote>
          Selecionar a variante aplicável ao modelo de negócios (B2B exclusivo
          ou B2B/B2C) antes da publicação comercial.
        </LegalReviewNote>
      </section>

      <section
        id="termos-obrigacoes"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          3. Obrigações do Usuário e Conformidade com a LGPD
        </h2>
        <p>
          Ao inserir dados pessoais de terceiros (leads, prospects, contatos,
          gravações e mensagens) no Dotus CRM, o Cliente atua como{" "}
          <strong>Controlador</strong> (Art. 5º, VI da Lei nº 13.709/2018) e
          declara que:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            Possui base legal válida (Art. 7º da LGPD) para a coleta,
            armazenamento e abordagem comercial de cada contato cadastrado ou
            importado via CSV/API;
          </li>
          <li>
            Não utilizará o CRM nem suas integrações de telefonia, WhatsApp ou
            e-mail para envio de comunicações ilícitas, abusivas ou sem opção de
            descadastramento (<em>opt-out</em>);
          </li>
          <li>
            Caso ative o módulo opcional de gravação e transcrição de chamadas,
            compromete-se a informar previamente o interlocutor sobre a gravação
            da conversa.
          </li>
        </ul>
      </section>

      <section
        id="termos-responsabilidade"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          4. Responsabilidade dos Agentes de Tratamento (Arts. 39 e 42 da LGPD)
        </h2>
        <p>
          Nos termos dos{" "}
          <strong>Arts. 39 e 42, § 1º, inciso I da Lei nº 13.709/2018</strong>,
          a Dotus, enquanto Operadora dos dados inseridos pelo Cliente, realiza
          o tratamento segundo as instruções lícitas fornecidas pelo Controlador
          e responde solidariamente apenas quando descumprir as obrigações da
          legislação de proteção de dados ou quando não tiver seguido as
          instruções lícitas do Controlador.
        </p>
        <LegalReviewNote>
          Validar cláusula de limitação de responsabilidade patrimonial e
          direito de regresso entre Controlador e Operador nos termos do Art. 42
          da LGPD e do Código Civil.
        </LegalReviewNote>
      </section>

      <section
        id="termos-isolamento"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          5. Isolamento Multi-Tenant e Segurança de Credenciais
        </h2>
        <p>
          Cada organização assinante opera em ambiente logicamente isolado por
          identificador de tenant (<code className="font-mono text-sm">crm_id</code>). O
          Cliente é responsável por manter o sigilo de suas senhas e chaves de
          API (<code className="font-mono text-sm">x-api-key</code>) e por revogar
          imediatamente credenciais de colaboradores desligados.
        </p>
      </section>

      <section
        id="termos-foro"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4"
      >
        <h2 className="text-lg font-bold text-foreground">
          6. Foro e Legislação Aplicável
        </h2>
        <p>
          Estes Termos são regidos pelas leis da República Federativa do Brasil.
          Fica eleito o foro da Comarca de{" "}
          <strong>{LEGAL_COMPANY_INFO.foroComarca}</strong> para dirimir
          eventuais controvérsias oriundas deste instrumento, ressalvado o foro
          do domicílio do consumidor quando aplicável o Art. 101, I da Lei nº
          8.078/1990 (CDC).
        </p>
        <LegalReviewNote>
          Confirmar a comarca de eleição de foro no contrato social da empresa.
        </LegalReviewNote>
      </section>
    </LegalPageShell>
  );
};

// ─── 3. POLÍTICA DE CANCELAMENTO E REEMBOLSO (/cancelamento-reembolso) ───────
const REFUND_TOC: TocItem[] = [
  { id: "reembolso-regras", label: "1. Regras conforme Perfil (B2B vs. B2C)" },
  { id: "reembolso-solicitacao", label: "2. Como Solicitar o Cancelamento" },
  { id: "reembolso-exportacao", label: "3. Exportação e Destinação dos Dados" },
];

export const RefundCancellationPage = () => {
  return (
    <LegalPageShell
      title="Política de Cancelamento e Reembolso"
      subtitle={`Última atualização: ${LEGAL_COMPANY_INFO.dataUltimaAtualizacao}`}
      icon={<RefreshCcw className="w-5 h-5" aria-hidden="true" />}
      toc={REFUND_TOC}
    >
      <section
        id="reembolso-regras"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          1. Regras Aplicáveis conforme o Perfil de Contratação (B2B vs. B2C)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-border bg-card space-y-2">
            <h3 className="font-semibold text-foreground text-base">
              Variante A — Contratos Empresariais (B2B — Código Civil)
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Nas contratações realizadas entre pessoas jurídicas para uso
              empresarial, a resilição unilateral (cancelamento) segue o
              disposto no{" "}
              <strong className="text-foreground">
                Art. 473 da Lei nº 10.406/2002 (Código Civil)
              </strong>{" "}
              e as condições comerciais da proposta/plano contratado. Não se
              aplica automaticamente o direito de arrependimento consumerista,
              salvo previsão expressa em proposta comercial.
            </p>
          </div>
          <div className="p-4 rounded-xl border border-border bg-card space-y-2">
            <h3 className="font-semibold text-foreground text-base">
              Variante B — Contratação por Consumidor (B2C — Art. 49 do CDC)
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Quando configurada relação de consumo (Art. 2º da Lei nº
              8.078/1990) contratada fora do estabelecimento comercial (pela
              internet), é assegurado o{" "}
              <strong className="text-foreground">
                direito de arrependimento no prazo de 7 (sete) dias corridos
              </strong>{" "}
              a contar da assinatura ou do início da disponibilização do
              serviço, nos termos do{" "}
              <strong className="text-foreground">
                Art. 49 da Lei nº 8.078/1990 (CDC) e do Art. 5º do Decreto nº
                7.962/2013
              </strong>
              , com reembolso integral dos valores eventualmente pagos.
            </p>
          </div>
        </div>
        <LegalReviewNote>
          Ajustar a política de cancelamento, aviso prévio e eventual multa
          proporcional conforme o modelo comercial da Dotus (B2B ou B2C).
        </LegalReviewNote>
      </section>

      <section
        id="reembolso-solicitacao"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          2. Como Solicitar o Cancelamento
        </h2>
        <p>
          O cancelamento da assinatura pode ser solicitado a qualquer momento
          pelo administrador da conta através do canal oficial de atendimento:{" "}
          <strong>{LEGAL_COMPANY_INFO.emailSuporte}</strong>. A confirmação do
          recebimento da solicitação será enviada imediatamente ao solicitante
          (conforme Art. 5º, § 2º do Decreto nº 7.962/2013 quando aplicável).
        </p>
      </section>

      <section
        id="reembolso-exportacao"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4"
      >
        <h2 className="text-lg font-bold text-foreground">
          3. Exportação e Destinação dos Dados Após o Encerramento (Arts. 15 e
          16 da LGPD)
        </h2>
        <p>
          Após o encerramento do contrato, o Cliente disporá do prazo de{" "}
          <strong>
            {LEGAL_COMPANY_INFO.prazoExportacaoAposCancelamento}
          </strong>{" "}
          para exportar sua base de contatos, empresas e negócios em formato
          estruturado (JSON/CSV). Encerrado esse prazo, os dados de negócio do
          tenant serão eliminados ou anonimizados (Art. 15 e Art. 16 da Lei nº
          13.709/2018), mantendo-se apenas os{" "}
          <strong>registros de acesso a aplicações</strong> pelo prazo legal
          mínimo de 6 meses (Art. 15 da Lei nº 12.965/2014 — Marco Civil da
          Internet) e eventuais documentos fiscais exigidos por lei (Art. 16, I
          da LGPD).
        </p>
      </section>
    </LegalPageShell>
  );
};

// ─── 4. POLÍTICA DE COOKIES (/cookies) ───────────────────────────────────────
const COOKIES_TOC: TocItem[] = [
  { id: "cookies-conceito", label: "1. O que são Cookies e Como os Utilizamos" },
  { id: "cookies-inventario", label: "2. Inventário Técnico de Cookies e LocalStorage" },
  { id: "cookies-status", label: "3. Seu Status Atual de Consentimento" },
];

export const CookiePolicyPage = () => {
  const currentConsent = getCookieConsent();

  return (
    <LegalPageShell
      title="Política de Cookies e Armazenamento Local"
      subtitle={`Versão ${LEGAL_COMPANY_INFO.versaoPoliticaCookies} • Última atualização: ${LEGAL_COMPANY_INFO.dataUltimaAtualizacao}`}
      icon={<Cookie className="w-5 h-5" aria-hidden="true" />}
      toc={COOKIES_TOC}
    >
      <section
        id="cookies-conceito"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          1. O que são Cookies e Como os Utilizamos
        </h2>
        <p>
          Em conformidade com o{" "}
          <strong>
            Art. 7º, inciso VIII da Lei nº 12.965/2014 (Marco Civil da Internet)
          </strong>
          , o <strong>Art. 9º da Lei nº 13.709/2018 (LGPD)</strong> e o{" "}
          <em>Guia Orientativo de Cookies e Proteção de Dados Pessoais</em> da
          Autoridade Nacional de Proteção de Dados (ANPD), informamos de forma
          transparente todos os cookies e chaves de{" "}
          <code className="font-mono text-sm">localStorage</code> utilizados
          pelo Dotus CRM.
        </p>
      </section>

      <section
        id="cookies-inventario"
        tabIndex={-1}
        className="scroll-mt-24 space-y-4 border-b border-border/60 pb-8"
      >
        <h2 className="text-lg font-bold text-foreground">
          2. Inventário Técnico de Cookies e Armazenamento Local
        </h2>
        <div className="overflow-x-auto border border-border rounded-xl">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted text-foreground font-semibold border-b border-border">
              <tr>
                <th className="p-3">Nome / Chave</th>
                <th className="p-3">Categoria</th>
                <th className="p-3">Finalidade</th>
                <th className="p-3">Duração / Retenção</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-foreground">
              <tr>
                <td className="p-3 font-mono text-xs">sb-*-auth-token</td>
                <td className="p-3">Estritamente Necessário</td>
                <td className="p-3">
                  Mantém a sessão autenticada do usuário no Supabase Auth.
                </td>
                <td className="p-3">Sessão / Renovação de login</td>
              </tr>
              <tr>
                <td className="p-3 font-mono text-xs">dotus_voip_session</td>
                <td className="p-3">Estritamente Necessário</td>
                <td className="p-3">
                  Cookie HTTP-Only criptografado (AES-256-GCM) para sessão do
                  ramal VoIP quando conectado pelo operador.
                </td>
                <td className="p-3">Sessão (renovado a cada 55 min)</td>
              </tr>
              <tr>
                <td className="p-3 font-mono text-xs">
                  sidebar_state / crm_active_id
                </td>
                <td className="p-3">Estritamente Necessário</td>
                <td className="p-3">
                  Preserva o estado visual da barra lateral e o tenant ativo
                  selecionado pelo usuário.
                </td>
                <td className="p-3">Persistente local</td>
              </tr>
              <tr>
                <td className="p-3 font-mono text-xs">
                  dotus_crm_cookie_consent_v1
                </td>
                <td className="p-3">Estritamente Necessário</td>
                <td className="p-3">
                  Registra a decisão do usuário sobre o aceite ou rejeição de
                  cookies opcionais.
                </td>
                <td className="p-3">12 meses</td>
              </tr>
              <tr>
                <td className="p-3 font-mono text-xs">
                  Analytics / Telemetria
                </td>
                <td className="p-3">Opcional (Analytics)</td>
                <td className="p-3">
                  Métricas técnicas de uso da aplicação.{" "}
                  <strong>Desativado por padrão</strong>; só é executado após
                  consentimento expresso.
                </td>
                <td className="p-3">Apenas sob opt-in</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        id="cookies-status"
        tabIndex={-1}
        className="scroll-mt-24 p-5 rounded-xl border border-border bg-card space-y-4"
      >
        <h2 className="text-lg font-bold text-foreground">
          3. Seu Status Atual de Consentimento
        </h2>
        {currentConsent ? (
          <div className="text-sm text-foreground space-y-1.5">
            <p>
              • <strong>Cookies Necessários:</strong> Ativos (Obrigatórios para
              funcionamento)
            </p>
            <p>
              • <strong>Cookies de Analytics:</strong>{" "}
              {currentConsent.analytics ? "Autorizados" : "Rejeitados"}
            </p>
            <p>
              • <strong>Cookies de Marketing:</strong>{" "}
              {currentConsent.marketing ? "Autorizados" : "Rejeitados"}
            </p>
            <p>
              • <strong>Data da escolha:</strong>{" "}
              {new Date(currentConsent.decidedAt).toLocaleString("pt-BR")}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Você ainda não registrou uma preferência personalizada (apenas
            cookies estritamente necessários estão ativos).
          </p>
        )}

        <div className="no-print">
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={openCookiePreferences}
            className="text-xs cursor-pointer"
          >
            Alterar minhas preferências de cookies
          </Button>
        </div>
      </section>
    </LegalPageShell>
  );
};
